import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import ShopLogo from "../images/shop_logo.png";
import { SHOP_NAME, SHOP_ADDRESS, SHOP_PHONE } from "../Variables.js";

Modal.setAppElement('#root');

const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

// Natural sort by account code
const sortByCode = (a, b) => {
  const codeA = (a.code || '').toString();
  const codeB = (b.code || '').toString();
  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
};

// Accurate categorization using your real account structure
const categorizeIncomeStatementAccounts = (accounts, balances) => {
  const categories = {
    revenue: [],
    costOfGoodsSold: [],
    operatingExpenses: [],
    otherIncome: [],
    otherExpenses: [],
    tax: []
  };

  accounts.forEach(account => {
    // CRITICAL: Only include Income Statement accounts
    if (account.financialStatement !== "Income Statement") return;
    if (!account.active) return;

    const balance = balances[account.id] || 0;
    if (Math.abs(balance) < 0.01) return;

    const name = (account.name || '').toLowerCase();
    const type = (account.type || '').toLowerCase();
    const code = (account.code || '').toString();

    const accountData = {
      ...account,
      balance: Math.abs(balance) // We display absolute values
    };

    // REVENUE: Credit balance (negative in your system)
    if (balance < 0 && (
      type.includes('revenue') ||
      type.includes('income') ||
      name.includes('sales') ||
      name.includes('revenue') ||
      name.includes('service income') ||
      name.includes('fees')
    )) {
      categories.revenue.push(accountData);
    }
    // OTHER INCOME: Also credit balance
    else if (balance < 0 && (
      name.includes('other income') ||
      name.includes('interest income') ||
      name.includes('gain') ||
      name.includes('discount received') ||
      name.includes('exchange gain')
    )) {
      categories.otherIncome.push(accountData);
    }
    // EXPENSES: Debit balance (positive in your system)
    else if (balance > 0) {
      if (
        name.includes('cost') && name.includes('good') ||
        name.includes('cogs') ||
        name.includes('purchase') ||
        type.includes('cost of goods') ||
        type.includes('direct cost')
      ) {
        categories.costOfGoodsSold.push(accountData);
      }
      else if (
        name.includes('interest expense') ||
        name.includes('loss') ||
        name.includes('bank charge') ||
        name.includes('other expense') ||
        name.includes('fine')
      ) {
        categories.otherExpenses.push(accountData);
      }
      else if (
        name.includes('tax') ||
        name.includes('income tax') ||
        name.includes('vat') && name.includes('payable') === false
      ) {
        categories.tax.push(accountData);
      }
      else if (
        type.includes('expense') ||
        name.includes('salary') ||
        name.includes('rent') ||
        name.includes('utility') ||
        name.includes('depreciation') ||
        name.includes('advertising') ||
        name.includes('transport') ||
        name.includes('maintenance')
      ) {
        categories.operatingExpenses.push(accountData);
      }
    }
  });

  // Sort all categories by code
  Object.keys(categories).forEach(key => {
    categories[key].sort(sortByCode);
  });

  return categories;
};

const IncomeStatement = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [incomeStatementData, setIncomeStatementData] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setDate(0);
    return date.toISOString().split('T')[0];
  });
  const [isFetching, setIsFetching] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  const accountsCollectionRef = collection(db, "accounts");
  const journalEntriesCollectionRef = collection(db, "journalEntries");

  useEffect(() => {
    if (!user || !userData) {
      setLoading(true);
      return;
    }
    setLoading(false);
    fetchAccounts();
  }, [user, userData]);

  useEffect(() => {
    if (accounts.length > 0) {
      generateIncomeStatement();
    }
  }, [startDate, endDate, accounts]);

  const fetchAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef);
      const data = await getDocs(q);
      setAccounts(data.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    } catch (err) {
      setError("Failed to fetch accounts");
      setShowModal(true);
    }
  };

  const fetchJournalEntries = async () => {
    if (!user?.uid) return [];
    try {
      const q = query(journalEntriesCollectionRef);
      const data = await getDocs(q);
      return data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    } catch (err) {
      setError("Failed to fetch journal entries");
      setShowModal(true);
      return [];
    }
  };

  const calculateAccountBalanceForPeriod = (accountId, accountName, entries) => {
    let balance = 0;
    entries.forEach(entry => {
      const entryDate = new Date(entry.date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (entryDate >= start && entryDate <= end) {
        entry.lines.forEach(line => {
          if (line.accountName === accountName || (line.accountId && line.accountId === accountId)) {
            balance += (Number(line.debit) || 0) - (Number(line.credit) || 0);
          }
        });
      }
    });
    return balance;
  };

  const generateIncomeStatement = async () => {
    if (accounts.length === 0) return;
    setIsFetching(true);
    try {
      const entries = await fetchJournalEntries();
      const balances = {};
      accounts.forEach(acc => {
        balances[acc.id] = calculateAccountBalanceForPeriod(acc.id, acc.name, entries);
      });

      const categorized = categorizeIncomeStatementAccounts(accounts, balances);

      const totalRevenue = categorized.revenue.reduce((s, a) => s + a.balance, 0);
      const totalCOGS = categorized.costOfGoodsSold.reduce((s, a) => s + a.balance, 0);
      const grossProfit = totalRevenue - totalCOGS;

      const totalOperatingExpenses = categorized.operatingExpenses.reduce((s, a) => s + a.balance, 0);
      const operatingProfit = grossProfit - totalOperatingExpenses;

      const totalOtherIncome = categorized.otherIncome.reduce((s, a) => s + a.balance, 0);
      const totalOtherExpenses = categorized.otherExpenses.reduce((s, a) => s + a.balance, 0);
      const netProfitBeforeTax = operatingProfit + totalOtherIncome - totalOtherExpenses;

      const totalTax = categorized.tax.reduce((s, a) => s + a.balance, 0);
      const netProfitAfterTax = netProfitBeforeTax - totalTax;

      setIncomeStatementData({
        categorizedAccounts: categorized,
        totals: {
          totalRevenue,
          totalCOGS,
          grossProfit,
          totalOperatingExpenses,
          operatingProfit,
          totalOtherIncome,
          totalOtherExpenses,
          netProfitBeforeTax,
          totalTax,
          netProfitAfterTax
        },
        period: { startDate, endDate }
      });
    } catch (err) {
      setError("Failed to generate income statement");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const generatePDF = () => {
    if (!incomeStatementData) return;
    const doc = new jsPDF();
    const { categorizedAccounts, totals, period } = incomeStatementData;
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let y = 10;
    try { doc.addImage(ShopLogo, "PNG", 14, y, 15, 15); } catch (e) {}
    doc.setFontSize(16).setFont("helvetica", "bold").text(SHOP_NAME, 35, y + 5);
    doc.setFontSize(14).setFont("helvetica", "normal").text("Income Statement", 35, y + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, y + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, y + 23);
    doc.text(`For the period ${dateFormatter.format(new Date(period.startDate))} to ${dateFormatter.format(new Date(period.endDate))}`, 35, y + 28);
    y += 35;

    const tableData = [];

    tableData.push([{ content: 'REVENUE', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }]);
    categorizedAccounts.revenue.forEach(r => tableData.push([r.name, `N${r.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]));
    tableData.push(['TOTAL REVENUE', { content: `N${totals.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);
    tableData.push(['', '']);

    if (categorizedAccounts.costOfGoodsSold.length > 0) {
      tableData.push([{ content: 'COST OF GOODS SOLD', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }]);
      categorizedAccounts.costOfGoodsSold.forEach(c => tableData.push([c.name, `(N${c.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })})`]));
      tableData.push(['TOTAL COST OF GOODS SOLD', { content: `(N${totals.totalCOGS.toLocaleString('en-US', { minimumFractionDigits: 2 })})`, styles: { fontStyle: 'bold' } }]);
      tableData.push(['GROSS PROFIT', { content: `N${totals.grossProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);
      tableData.push(['', '']);
    }

    if (categorizedAccounts.operatingExpenses.length > 0) {
      tableData.push([{ content: 'OPERATING EXPENSES', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }]);
      categorizedAccounts.operatingExpenses.forEach(e => tableData.push([e.name, `(N${e.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })})`]));
      tableData.push(['TOTAL OPERATING EXPENSES', { content: `(N${totals.totalOperatingExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })})`, styles: { fontStyle: 'bold' } }]);
      tableData.push(['OPERATING PROFIT', { content: `N${totals.operatingProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);
      tableData.push(['', '']);
    }

    if (categorizedAccounts.otherIncome.length > 0 || categorizedAccounts.otherExpenses.length > 0) {
      tableData.push([{ content: 'OTHER INCOME/(EXPENSES)', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }]);
      categorizedAccounts.otherIncome.forEach(i => tableData.push([i.name, `N${i.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]));
      categorizedAccounts.otherExpenses.forEach(e => tableData.push([e.name, `(N${e.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })})`]));
      tableData.push(['NET PROFIT BEFORE TAX', { content: `N${totals.netProfitBeforeTax.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);
      tableData.push(['', '']);
    }

    if (categorizedAccounts.tax.length > 0) {
      tableData.push([{ content: 'INCOME TAX EXPENSE', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }]);
      categorizedAccounts.tax.forEach(t => tableData.push([t.name, `(N${t.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })})`]));
      tableData.push(['', '']);
    }

    tableData.push([
      { content: 'NET PROFIT AFTER TAX', colSpan: 2, styles: { fontStyle: 'bold', fillColor: totals.netProfitAfterTax >= 0 ? [220, 237, 200] : [245, 220, 220] } }
    ]);
    tableData.push([
      'NET PROFIT AFTER TAX',
      { content: `N${Math.abs(totals.netProfitAfterTax).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }
    ]);

    autoTable(doc, {
      startY: y,
      head: [['DESCRIPTION', 'AMOUNT']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2, textColor: 0, },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 130 },
        1: { cellWidth: 60, halign: 'right' }
      },
      margin: { left: 14, right: 14 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          const text = data.cell.text.join('');
          if (text.includes('(')) data.cell.styles.textColor = [200, 0, 0];
          else if (text.startsWith('N')) data.cell.styles.textColor = [0, 100, 0];
        }
      }
    });

    doc.save(`IncomeStatement_${startDate}_to_${endDate}.pdf`);
  };

  const refreshIncomeStatement = () => {
    generateIncomeStatement();
    setSuccessMessage("Income Statement refreshed successfully");
    setShowModal(true);
  };

  if (loading) return <Loader text="Loading" />;
  if (!userData) return <Loader text="Reading" />;

  const allowedRoles = ['Accountant', 'Admin', 'Supreme'];

  return (
    <>
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Income Statement</h2>
            <Link to="#" className="home-link" onClick={refreshIncomeStatement}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed">
            {/* Controls */}
            <div className="column gap-15 align-center justify-center" style={{ marginTop: '10px' }}>
              <div className="row gap-1">
                <div className="column align-center">
                    <label className="input-label bold">From Date</label>
                    <input
                    type="date"
                    className="input-field1"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ marginBottom: '0px' }}
                    />
                </div>
                <div className="column align-center">
                    <label className="input-label bold">To Date</label>
                    <input
                    type="date"
                    className="input-field1"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ marginBottom: '0px' }}
                    />
                </div>
              </div> 
              <div className="row gap-1" style={{marginTop: '-25px', marginBottom: '10px',}}>
                {/* <button 
                    className="login-button login-button-d caps" 
                    onClick={() => setIsDateModalOpen(true)}
                    style={{ marginTop: '25px' }}
                >
                    Change Period
                </button> */}
                <button 
                    className="login-button login-button-d caps" 
                    onClick={generatePDF}
                    disabled={!incomeStatementData}
                >
                    DOWNLOAD
                </button>
              </div> 
            </div>

            {isFetching ? (
              <p className="loading-text">Generating Income Statement...</p>
            ) : incomeStatementData ? (
              <div className="income-statement-container scroll-fixed scroll-fixed2">
                {/* Profitability Indicator */}
                <div className={`profitability-status ${incomeStatementData.totals.netProfitAfterTax >= 0 ? 'profitable' : 'loss'}`}>
                  <div className="row align-center gap-1">
                    <i className={`fas ${incomeStatementData.totals.netProfitAfterTax >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                    <span className="bold">
                      {incomeStatementData.totals.netProfitAfterTax >= 0 ? 'PROFITABLE' : 'LOSS MAKING'}
                    </span>
                  </div>
                  <div className="net-profit-amount">
                    Net {incomeStatementData.totals.netProfitAfterTax >= 0 ? 'Profit' : 'Loss'}: 
                    <strong> N{Math.abs(incomeStatementData.totals.netProfitAfterTax).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                  </div>
                </div>

                {/* Income Statement Display */}
                <div className="income-statement-content">
                  
                  {/* REVENUE */}
                  <div className="income-statement-section">
                    <h3 className="income-statement-header">REVENUE</h3>
                    {incomeStatementData.categorizedAccounts.revenue.map(revenue => (
                      <div key={revenue.id} className="income-statement-item row space-between">
                        <span>{revenue.name}</span>
                        <span className="amount positive">N{revenue.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    ))}
                    <div className="income-statement-total row space-between">
                      <strong>Total Revenue</strong>
                      <strong className="amount positive">N{incomeStatementData.totals.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                    </div>
                  </div>

                  {/* COST OF GOODS SOLD */}
                  {incomeStatementData.categorizedAccounts.costOfGoodsSold.length > 0 && (
                    <div className="income-statement-section">
                      <h3 className="income-statement-header">COST OF GOODS SOLD</h3>
                      {incomeStatementData.categorizedAccounts.costOfGoodsSold.map(cogs => (
                        <div key={cogs.id} className="income-statement-item row space-between">
                          <span>{cogs.name}</span>
                          <span className="amount negative">(N{cogs.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
                        </div>
                      ))}
                      <div className="income-statement-total row space-between">
                        <strong>Total Cost of Goods Sold</strong>
                        <strong className="amount negative">(N{incomeStatementData.totals.totalCOGS.toLocaleString('en-US', { minimumFractionDigits: 2 })})</strong>
                      </div>
                      
                      {/* GROSS PROFIT */}
                      <div className="income-statement-subtotal row space-between">
                        <strong>GROSS PROFIT</strong>
                        <strong className="amount">N{incomeStatementData.totals.grossProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                      </div>
                    </div>
                  )}

                  {/* OPERATING EXPENSES */}
                  {incomeStatementData.categorizedAccounts.operatingExpenses.length > 0 && (
                    <div className="income-statement-section">
                      <h3 className="income-statement-header">OPERATING EXPENSES</h3>
                      {incomeStatementData.categorizedAccounts.operatingExpenses.map(expense => (
                        <div key={expense.id} className="income-statement-item row space-between">
                          <span>{expense.name}</span>
                          <span className="amount negative">(N{expense.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
                        </div>
                      ))}
                      <div className="income-statement-total row space-between">
                        <strong>Total Operating Expenses</strong>
                        <strong className="amount negative">(N{incomeStatementData.totals.totalOperatingExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })})</strong>
                      </div>
                      
                      {/* OPERATING PROFIT */}
                      <div className="income-statement-subtotal row space-between">
                        <strong>OPERATING PROFIT</strong>
                        <strong className="amount">N{incomeStatementData.totals.operatingProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                      </div>
                    </div>
                  )}

                  {/* OTHER INCOME/EXPENSES */}
                  {(incomeStatementData.categorizedAccounts.otherIncome.length > 0 || incomeStatementData.categorizedAccounts.otherExpenses.length > 0) && (
                    <div className="income-statement-section">
                      <h3 className="income-statement-header">OTHER INCOME/(EXPENSES)</h3>
                      {incomeStatementData.categorizedAccounts.otherIncome.map(income => (
                        <div key={income.id} className="income-statement-item row space-between">
                          <span>{income.name}</span>
                          <span className="amount positive">N{income.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                      {incomeStatementData.categorizedAccounts.otherExpenses.map(expense => (
                        <div key={expense.id} className="income-statement-item row space-between">
                          <span>{expense.name}</span>
                          <span className="amount negative">(N{expense.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
                        </div>
                      ))}
                      
                      {/* NET PROFIT BEFORE TAX */}
                      <div className="income-statement-subtotal row space-between">
                        <strong>NET PROFIT BEFORE TAX</strong>
                        <strong className="amount">N{incomeStatementData.totals.netProfitBeforeTax.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                      </div>
                    </div>
                  )}

                  {/* TAX */}
                  {incomeStatementData.categorizedAccounts.tax.length > 0 && (
                    <div className="income-statement-section">
                      <h3 className="income-statement-header">INCOME TAX EXPENSE</h3>
                      {incomeStatementData.categorizedAccounts.tax.map(tax => (
                        <div key={tax.id} className="income-statement-item row space-between">
                          <span>{tax.name}</span>
                          <span className="amount negative">(N{tax.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })})</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* NET PROFIT AFTER TAX */}
                  <div className={`income-statement-grand-total row space-between ${incomeStatementData.totals.netProfitAfterTax >= 0 ? 'profitable' : 'loss'}`}>
                    <strong>NET {incomeStatementData.totals.netProfitAfterTax >= 0 ? 'PROFIT' : 'LOSS'} AFTER TAX</strong>
                    <strong className="amount">
                      {incomeStatementData.totals.netProfitAfterTax >= 0 ? 'N' : '(N'}{Math.abs(incomeStatementData.totals.netProfitAfterTax).toLocaleString('en-US', { minimumFractionDigits: 2 })}{incomeStatementData.totals.netProfitAfterTax >= 0 ? '' : ')'}
                    </strong>
                  </div>
                </div>
              </div>
            ) : (
              <p className="loading-text">No data available for the selected period.</p>
            )}
          </div>

          {/* Date Selection Modal */}
          <Modal
            isOpen={isDateModalOpen}
            onRequestClose={() => setIsDateModalOpen(false)}
            contentLabel="Select Period"
            className="custom-modal"
            overlayClassName="custom-modal-overlay"
          >
            <h2>Select Income Statement Period</h2>
            <form className="session-form">
              <label className="input-label">From Date</label>
              <input
                type="date"
                className="input-field"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
              <label className="input-label">To Date</label>
              <input
                type="date"
                className="input-field"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
              />
              <div className="row align-center gap-1">
                <button
                  className="login-button"
                  type="button"
                  onClick={() => setIsDateModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="login-button"
                  type="button"
                  onClick={() => {
                    setIsDateModalOpen(false);
                    generateIncomeStatement();
                  }}
                >
                  Apply Period
                </button>
              </div>
            </form>
          </Modal>

          {showModal && (
            <SuccessModal
              icon={error ? "xmark" : "check"}
              iconColor={error ? "red" : "green"}
              message={error || successMessage}
              onClose={() => {
                setError("");
                setSuccessMessage("");
                setShowModal(false);
              }}
            />
          )}
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      <style jsx>{`
        .profitability-status {
          padding: 15px;
          border-radius: 8px;
          margin: 10px 0;
          text-align: center;
          width: 100%;
        }
        .profitable {
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }
        .loss {
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }
        .net-profit-amount {
          margin-top: 10px;
          font-size: 18px;
        }
        .income-statement-container {
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
        }
        .income-statement-content {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .income-statement-section {
          margin-bottom: 25px;
        }
        .income-statement-header {
          color: #2c3e50;
          border-bottom: 2px solid #3498db;
          padding-bottom: 8px;
          margin-bottom: 15px;
        }
        .income-statement-item {
          padding: 8px 0;
          border-bottom: 1px dotted #dee2e6;
        }
        .income-statement-total {
          padding: 12px 0;
          border-top: 2px solid #adb5bd;
          margin-top: 10px;
        }
        .income-statement-subtotal {
          padding: 15px 0;
          border-top: 2px solid #2c3e50;
          margin-top: 15px;
          font-size: 16px;
        }
        .income-statement-grand-total {
          padding: 20px 0;
          border-top: 3px double #2c3e50;
          margin-top: 20px;
          font-size: 18px;
        }
        .income-statement-grand-total.profitable {
          color: #155724;
          background-color: #d4edda;
          padding: 15px;
          border-radius: 4px;
        }
        .income-statement-grand-total.loss {
          color: #721c24;
          background-color: #f8d7da;
          padding: 15px;
          border-radius: 4px;
        }
        .amount {
          font-family: monospace;
          font-weight: bold;
        }
        .amount.positive {
          color: #155724;
        }
        .amount.negative {
          color: #721c24;
        }
        
        @media (max-width: 768px) {
          .income-statement-content {
            padding: 15px;
          }
          .income-statement-item,
          .income-statement-total,
          .income-statement-subtotal {
            font-size: 14px;
          }
        }
      `}</style>
    </>
  );
};

export default IncomeStatement;
